/**
 * LogsChart - Column chart component for log volume visualization
 */
import { ReactElement, useMemo, useCallback, useState, useEffect } from 'react'
import createPlotlyComponent from 'react-plotly.js/factory'
import { Flex, Spin } from 'antd'
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons'
import moment from 'moment'

// Create Plot component using global Plotly from CDN (lazy initialization)
type PlotComponent =
  ReturnType<typeof createPlotlyComponent>
let Plot: PlotComponent | null = null

const getPlotComponent = (): PlotComponent | null => {
  if (!Plot && window.Plotly) {
    Plot = createPlotlyComponent(window.Plotly)
  }
  return Plot
}

import { setCustomDateFilter } from 'actions/search/filters'
import { getCommonQueryFilters } from 'selectors/search/filters'
import { TimeInterval } from 'types/search/chart'
import { TimeRange } from 'types/search/filters'
import { calculateEffectiveDateRange } from 'lib/search/calculate-effective-date-range'
import {
  getIntervalMinutes,
  getIntervalDuration,
  calculateBarInterval,
  formatInterval,
  formatHoverText,
} from './chart-utils'
import {
  calculateIntervalMinutes,
  calculateNewStartDate,
  canZoomIn,
  canZoomOut,
  zoomIn,
  zoomOut,
} from './zoom-utils'
import styles from './LogsChart.module.scss'

export interface LogsChartData {
  date: string
  value: number
}

export interface LogsChartProps {
  data: LogsChartData[]
  revision?: number
  interval: TimeInterval | null
  timeRange?: TimeRange
  customDateRange?: {
    startDate: string | null
    endDate: string | null
  }
  isFetching?: boolean
}

// Default empty date range (to avoid creating new objects)
const DEFAULT_DATE_RANGE = { startDate: null, endDate: null }

/**
 * LogsChart component - displays log volume over time as a column chart
 * Uses revision as key to force remount on resize
 * Supports interactive zoom and click-to-filter functionality
 */
const LogsChart = (props: LogsChartProps): ReactElement => {
  const {
    data,
    revision = 0,
    interval,
    timeRange = TimeRange.TEN_MIN,
    customDateRange = DEFAULT_DATE_RANGE,
    isFetching = false,
  } = props

  // Check if Plotly is loaded
  const [plotlyReady, setPlotlyReady] = useState(!!window.Plotly)

  // wait to load Plotly library
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.Plotly && !plotlyReady) {
        setPlotlyReady(true)
      }
    }, 1000) // check every second

    return () => clearInterval(interval)
  }, [plotlyReady])

  const maxValue = useMemo(
    () =>
      data.length
        ? data.reduce(
            (max, item): number => Math.max(max, item.value),
            data[0].value,
          )
        : 0,
    [data],
  )

  // Use the interval provided from Redux (calculated during fetch)
  // Fallback to ONE_MINUTE if not provided
  const timeInterval = interval || TimeInterval.ONE_MINUTE

  // Generate custom text for each bar (hover tooltip)
  const customText = useMemo(() => {
    return data.map((item) => {
      const { amount, unit } = getIntervalDuration(timeInterval)
      const start = moment(item.date)
      const end = moment(start).add(amount, unit)
      return formatHoverText(item.date, end.toISOString())
    })
  }, [data, timeInterval])

  // Calculate bar width in milliseconds based on time interval

  // Calculate X-axis range using the same logic as data fetching
  const xAxisRange = useMemo(() => {
    const effectiveDateRange = calculateEffectiveDateRange(
      timeRange,
      customDateRange.startDate,
      customDateRange.endDate,
    )

    const startDate = moment(effectiveDateRange.startDate).toDate()
    const endDate = moment(effectiveDateRange.endDate).toDate()

    return [startDate, endDate]
  }, [timeRange, customDateRange])

  const barWidth = useMemo(() => {
    const width = getIntervalMinutes(timeInterval)
    return width === 1000 && moment(xAxisRange[1]).diff(xAxisRange[0]) === 1000
      ? 2 * width
      : width
  }, [timeInterval, xAxisRange])

  // // Determine X-axis tick format based on interval duration
  // const xAxisTickFormat = useMemo(() => {
  //   // For intervals >= 1 day, show day of week + date
  //   if (timeInterval === TimeInterval.ONE_DAY) {
  //     return '%a %b %d' // Mon Nov 7
  //   }
  //   // For intervals >= 3 hours, show date + time
  //   if (
  //     timeInterval === TimeInterval.THREE_HOURS ||
  //     timeInterval === TimeInterval.ONE_HOUR
  //   ) {
  //     return '%b %d %H:%M' // Nov 7 14:00
  //   }
  //   // For shorter intervals, show just time
  //   return '%H:%M' // 14:00
  // }, [timeInterval])

  /**
   * Handle click on a bar - zoom to that bar's exact time interval
   */
  const handleClick = useCallback(
    (event: Readonly<Plotly.PlotMouseEvent>) => {
      if (event.points && event.points.length > 0) {
        const clickedDate = event.points[0].x as Date
        const [startDate, endDate] = calculateBarInterval(
          clickedDate,
          timeInterval,
        )

        // Set custom date filter (automatically sets timeRange to CUSTOM)
        setCustomDateFilter(startDate.toISOString(), endDate.toISOString())
      }
    },
    [timeInterval],
  )

  /**
   * Handle zoom/pan via box select - update date filter with new range
   */
  const handleRelayout = useCallback(
    (event: Readonly<Plotly.PlotRelayoutEvent>) => {
      // Check if this is a zoom/pan event (not a resize)
      if ('xaxis.range[0]' in event && 'xaxis.range[1]' in event) {
        const newStart = new Date(
          event['xaxis.range[0]'] as string | number | Date,
        )
        const newEnd = new Date(
          event['xaxis.range[1]'] as string | number | Date,
        )

        // Set custom date filter (automatically sets timeRange to CUSTOM)
        setCustomDateFilter(newStart.toISOString(), newEnd.toISOString())
      }
    },
    [],
  )

  // Calculate total events
  const totalEvents = useMemo(() => {
    return data.reduce((sum, item) => sum + item.value, 0)
  }, [data])

  // Format number with thousands separator
  const formattedTotalEvents = useMemo(() => {
    return totalEvents.toLocaleString('en-US')
  }, [totalEvents])

  // Format the X-axis range for display
  const dateRangeLabel = useMemo(() => {
    if (!xAxisRange || xAxisRange.length < 2) return ''
    const startISO = moment(xAxisRange[0]).toISOString()
    const endISO = moment(xAxisRange[1]).toISOString()
    return formatInterval(startISO, endISO)
  }, [xAxisRange])

  // Calculate current interval and check zoom capabilities
  const { canZoomInFlag, canZoomOutFlag } = useMemo(() => {
    const filters = getCommonQueryFilters()
    const interval = calculateIntervalMinutes(
      filters.dateRange.from,
      filters.dateRange.to,
    )
    return {
      currentInterval: interval,
      canZoomInFlag: canZoomIn(interval),
      canZoomOutFlag: canZoomOut(interval),
    }
  }, [])

  /**
   * Handle zoom in - decrease interval (keep endpoint fixed)
   */
  const handleZoomIn = useCallback(() => {
    if (isFetching) return

    const filters = getCommonQueryFilters()
    const currentIntervalMinutes = calculateIntervalMinutes(
      filters.dateRange.from,
      filters.dateRange.to,
    )

    const newIntervalMinutes = zoomIn(currentIntervalMinutes)
    if (newIntervalMinutes === null) return // Already at min zoom

    const endDate = filters.dateRange.to
    const newStartDate = calculateNewStartDate(endDate, newIntervalMinutes)

    setCustomDateFilter(newStartDate, endDate)
  }, [isFetching])

  /**
   * Handle zoom out - increase interval (keep endpoint fixed)
   */
  const handleZoomOut = useCallback(() => {
    if (isFetching) return

    const filters = getCommonQueryFilters()
    const currentIntervalMinutes = calculateIntervalMinutes(
      filters.dateRange.from,
      filters.dateRange.to,
    )

    const newIntervalMinutes = zoomOut(currentIntervalMinutes)
    if (newIntervalMinutes === null) return // Already at max zoom

    const endDate = filters.dateRange.to
    const newStartDate = calculateNewStartDate(endDate, newIntervalMinutes)

    setCustomDateFilter(newStartDate, endDate)
  }, [isFetching])

  // Get Plot component (lazy initialization)
  const PlotComponent = getPlotComponent()

  // Show loading if Plotly is not ready yet
  if (!plotlyReady || !PlotComponent) {
    return (
      <Flex className={styles.chartContainer}>
        <div className={styles.loadingOverlay}>
          <Spin size="large" tip="Loading chart library..." />
        </div>
      </Flex>
    )
  }

  return (
    <Flex className={styles.chartContainer}>
      {/* Chart header with date range and zoom controls */}
      <div className={styles.chartHeader}>
        <span className={styles.eventsCount}>
          {formattedTotalEvents} event{totalEvents !== 1 ? 's' : ''}
        </span>
        <div className={styles.rightSection}>
          <span className={styles.intervalLabel}>{dateRangeLabel}</span>
          <div className={styles.zoomButtons}>
            <ZoomInOutlined
              className={styles.zoomIcon}
              title="Zoom in"
              onClick={handleZoomIn}
              style={{
                opacity: !canZoomInFlag || isFetching ? 0.5 : 1,
                cursor:
                  !canZoomInFlag || isFetching ? 'not-allowed' : 'pointer',
              }}
            />
            <ZoomOutOutlined
              className={styles.zoomIcon}
              title="Zoom out"
              onClick={handleZoomOut}
              style={{
                opacity: !canZoomOutFlag || isFetching ? 0.5 : 1,
                cursor:
                  !canZoomOutFlag || isFetching ? 'not-allowed' : 'pointer',
              }}
            />
          </div>
        </div>
      </div>

      {isFetching && (
        <div className={styles.loadingOverlay}>
          <Spin size="large" />
        </div>
      )}
      <PlotComponent
        key={revision}
        data={[
          {
            x: data.map((d) => moment(d.date).toDate()),
            y: data.map((d) => d.value),
            type: 'bar',
            width: barWidth,
            marker: {
              color: '#1890ff',
            },
            customdata: customText,
            hovertemplate:
              '<b>%{customdata}</b><br>' +
              'Events: %{y}<br>' +
              '<extra></extra>',
          },
        ]}
        layout={{
          autosize: true,
          margin: { l: 40, r: 20, t: 20, b: 40 },
          xaxis: {
            automargin: true,
            range: xAxisRange,
            type: 'date',
            // tickformat: xAxisTickFormat,
          },
          yaxis: {
            automargin: true,
            gridcolor: '#f0f0f0',
            fixedrange: true,
            tick0: 0,
            rangemode: 'tozero',
            tickformat: 'd',
            tickmode: maxValue <= 6 ? 'linear' : undefined,
          },
          hovermode: 'x',
          paper_bgcolor: 'white',
          plot_bgcolor: 'white',
          dragmode: 'zoom',
        }}
        config={{
          displayModeBar: false,
          responsive: true,
        }}
        className={styles.plotContainer}
        useResizeHandler={true}
        onClick={handleClick}
        onRelayout={handleRelayout}
      />
    </Flex>
  )
}

export default LogsChart
